import {Emitter} from '@wonderlandengine/api';

export function NotifyPropertyChange(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalSet = descriptor.set;

    descriptor.set = function (value: any) {
        if (originalSet) {
            originalSet.call(this, value);
            this.notifyPropertyChanged(propertyKey);
        }
    };
}

export function NotifyPropertyChangeField<T>(target: any, propertyKey: string) {
    let value: T;

    const getter = function () {
        return value;
    };

    const setter = function (newVal: T) {
        value = newVal;
        this.notifyPropertyChanged(propertyKey);
    };

    // Delete the original property so we can define the new ones.
    if (delete target[propertyKey]) {
        Object.defineProperty(target, propertyKey, {
            get: getter,
            set: setter,
            enumerable: true,
            configurable: true,
        });
    }
}

// type ExcludeBaseKeys<T, U> = {
//     [K in keyof T]: K extends keyof U ? K : never;
// }[keyof T];

/**
 * A class that can be inherited from to get property change notifications.
 * 
 * @example
 * ```typescript
 *
class ExampleState extends NotifyPropertyChanged<ExampleState> {
    private _someValue = 1;
    set someValue(value) {
        const oldvalue = this._someValue;
        this._someValue = value;
        this.notifyPropertyChanged('someValue', value, oldvalue);
    }
    get someValue() {
        return this._someValue;
    }
}

class ExampleHandler {
    state: ExampleState = new ExampleState();

    constructor() {
        this.state.getPropertyEmitter('someValue').add((val, oldval) => {
            console.log(`someValue changed. new value = ${val}. old value = ${oldval}`);
        });
    }
}
*/
export class NotifyPropertyChanged<T> {
    propertyChanged = new Emitter<[keyof T]>();
    private propertyChangedTargeted: Record<keyof T, Emitter<[T[keyof T], T[keyof T]]>> = {} as Record<
        keyof T,
        Emitter<[T[keyof T], T[keyof T]]>
    >;
    private _isInitialized = false;

    init() {
        if (this._isInitialized) {
            return;
        }
        this._isInitialized = true;
        const propertyNames = [
            ...Object.getOwnPropertyNames(this),
            ...Object.getOwnPropertyNames(Object.getPrototypeOf(this)),
        ] as (keyof T)[];

        for (const key of propertyNames) {
            if (key !== 'propertyChanged' && key !== 'propertyChangedTargeted' && !key.toString().startsWith('_')) {
                //@ts-ignore
                if (typeof this[key] !== 'function') {
                    // Exclude methods
                    this.propertyChangedTargeted[key] = new Emitter<[T[keyof T], T[keyof T]]>();
                }
            }
        }
    }
    notifyPropertyChanged<K extends keyof T>(propertyName: K, newValue?: T[K], oldValue?: T[K]) {
        this.propertyChanged.notify(propertyName);

        if (this.propertyChangedTargeted[propertyName]) {
            this.propertyChangedTargeted[propertyName].notify(newValue, oldValue);
        }
    }

    changeOnProperty<K extends keyof T>(propertyName: K) {
        if (!this.propertyChangedTargeted[propertyName]) {
            this.init();
        }
        return this.propertyChangedTargeted[propertyName];
    }
}
